// apps/web/src/lib/estoque/inventario-revisao.ts — Fase 13, Plano 29 (EST-13, D-24, D-25, D-26).
// Lógica PURA da revisão e da aprovação do inventário (sem React): ordenação, filtro, textos de diferença, resumo do
// rodapé, segregação de funções na interface (a decisão final é do servidor), justificativas por linha e o corpo do POST.
import type {
  AprovarInventarioInput,
  AutorContagem,
  DonoTipo,
  ItemRevisao,
  ResultadoAprovacaoInventario,
  TipoAjusteInventario,
} from '@pieltec/contracts';
import { formatarQuantidade } from '../vendas/formatacao';
import { formatarValor } from './formatacao';
import { PERMISSAO_APROVAR_AJUSTE } from './inventario';

export const MENSAGEM_SEM_DIFERENCA = 'Sem diferença';
export const PLACEHOLDER_JUSTIFICATIVA = 'Por que o saldo difere?';
export const MENSAGEM_JUSTIFICATIVA_OBRIGATORIA = 'Informe por que o saldo difere.';
export const APOIO_SALDO_ESPERADO = 'Saldo esperado considera as entradas e saídas feitas desde o início da contagem.';
export const APOIO_CORTE =
  'O saldo esperado foi capturado no instante em que cada linha foi salva. A coluna "Movimentado durante a contagem" reúne as entradas e saídas feitas depois desse instante; elas não entram na diferença.';
export const APOIO_SOBRA_LOTE = 'Sobra de lote: fica registrada como divergência, sem entrada automática.';
export const MENSAGEM_AUTOR_NAO_APROVA = 'Quem fez a contagem não pode aprovar este ajuste.';
export const MENSAGEM_SEM_PERMISSAO_APROVAR = 'Você não tem permissão para aprovar ajustes de inventário.';
export const TEXTO_DESCARTAR = 'A contagem será descartada e nenhum ajuste será gerado. O motivo fica registrado.';
export const MENSAGEM_LOCALIZACAO_ALTERADA = 'O lote mudou de localização depois da contagem.';

const MENOS = '−';

/** Linhas com diferença primeiro (a ordem original é mantida dentro de cada grupo). */
export function ordenarRevisao(itens: readonly ItemRevisao[]): readonly ItemRevisao[] {
  const comDiferenca = itens.filter((item) => item.diferenca !== 0);
  const semDiferenca = itens.filter((item) => item.diferenca === 0);
  return [...comDiferenca, ...semDiferenca];
}

/** "Mostrar só itens com diferença": ligado esconde as linhas sem diferença. */
export function filtrarComDiferenca(itens: readonly ItemRevisao[], soComDiferenca: boolean): readonly ItemRevisao[] {
  return soComDiferenca ? itens.filter((item) => item.diferenca !== 0) : itens;
}

/** `+{N} {un}` / `−{N} {un}` (sinal de menos tipográfico); zero é "Sem diferença". */
export function textoDiferenca(diferenca: number, unidade: string): string {
  if (diferenca === 0) return MENSAGEM_SEM_DIFERENCA;
  const sufixo = unidade.trim() === '' ? '' : ` ${unidade.trim()}`;
  const modulo = formatarQuantidade(Math.abs(diferenca), unidade);
  return `${diferenca > 0 ? '+' : MENOS}${modulo}${sufixo}`;
}

/** Impacto no valor com sinal (`+R$ 10,00` / `−R$ 10,00`); sem custo é traço. */
export function textoImpacto(impactoValor: number | null): string {
  if (impactoValor === null) return '—';
  if (impactoValor === 0) return formatarValor(0);
  return `${impactoValor > 0 ? '+' : MENOS}${formatarValor(Math.abs(impactoValor))}`;
}

/** Movimentado depois do corte, com sinal e unidade (`+2 kg`); zero é "Nenhum". */
export function textoMovimentado(movimentado: number, unidade: string): string {
  return movimentado === 0 ? 'Nenhum' : textoDiferenca(movimentado, unidade);
}

const GERA_ENTRADA: ReadonlySet<TipoAjusteInventario> = new Set(['SALDO_ABERTURA', 'AJUSTE_ENTRADA']);
const GERA_SAIDA: ReadonlySet<TipoAjusteInventario> = new Set(['AJUSTE_SAIDA', 'SAIDA_LOTE']);

export interface ResumoRevisao {
  /** Linhas com diferença (inclui divergência de lote, que não gera movimento). */
  readonly comDiferenca: number;
  readonly entradas: number;
  readonly saidas: number;
  readonly divergenciasLote: number;
}

export function resumoRevisao(itens: readonly ItemRevisao[]): ResumoRevisao {
  let comDiferenca = 0;
  let entradas = 0;
  let saidas = 0;
  let divergenciasLote = 0;
  for (const item of itens) {
    if (item.tipoAjuste === 'NENHUM') continue;
    comDiferenca += 1;
    if (GERA_ENTRADA.has(item.tipoAjuste)) entradas += 1;
    else if (GERA_SAIDA.has(item.tipoAjuste)) saidas += 1;
    else if (item.tipoAjuste === 'DIVERGENCIA_LOTE') divergenciasLote += 1;
  }
  return { comDiferenca, entradas, saidas, divergenciasLote };
}

/** Rodapé fixo: "{N} itens com diferença · {P} entradas · {Q} saídas". */
export function textoResumoRevisao(resumo: ResumoRevisao): string {
  const itens = resumo.comDiferenca === 1 ? 'item' : 'itens';
  const entradas = resumo.entradas === 1 ? 'entrada' : 'entradas';
  const saidas = resumo.saidas === 1 ? 'saída' : 'saídas';
  return `${resumo.comDiferenca} ${itens} com diferença · ${resumo.entradas} ${entradas} · ${resumo.saidas} ${saidas}`;
}

/** Confirmação de "Aprovar ajustes" (sem aprovação parcial). */
export function textoConfirmacaoAprovar(resumo: Pick<ResumoRevisao, 'entradas' | 'saidas'>): string {
  const movimentos = resumo.entradas + resumo.saidas;
  return `Serão gerados ${movimentos} movimentos de ajuste (${resumo.entradas} entradas e ${resumo.saidas} saídas) com as justificativas informadas. O histórico não é editado; correções futuras são feitas por estorno.`;
}

/** Mensagem de sucesso depois de aprovar; divergências de lote (sem movimento) são avisadas à parte. */
export function textoSucessoAprovacao(resultado: Pick<ResultadoAprovacaoInventario, 'movimentosGerados' | 'divergenciasLote'>): string {
  const base = `Inventário ajustado: ${resultado.movimentosGerados} movimentos gerados.`;
  if (resultado.divergenciasLote <= 0) return base;
  const divergencias = resultado.divergenciasLote === 1 ? '1 sobra de lote registrada como divergência' : `${resultado.divergenciasLote} sobras de lote registradas como divergência`;
  return `${base} ${divergencias}, sem entrada automática.`;
}

// ---------------------------------------------------------------------------------------------------------------------
// Segregação de funções na interface (o servidor recusa de qualquer forma: 409 INVENTARIO_AUTOAPROVACAO)
// ---------------------------------------------------------------------------------------------------------------------

export interface EntradaPodeAprovar {
  readonly usuarioId: string | null;
  /** userId de quem salvou ao menos uma linha. Quem apenas enviou, sem contar, não entra nesta lista. */
  readonly autoresContagemIds: readonly string[];
  readonly permissoes: readonly string[];
}

export function ehAutorDaContagem(usuarioId: string | null, autoresContagemIds: readonly string[]): boolean {
  return usuarioId !== null && autoresContagemIds.includes(usuarioId);
}

/**
 * Ver a revisão (saldo esperado e diferença) exige `estoque:aprovar-ajuste` e não ter contado (CR-C03): o contador não vê
 * os números que a contagem cega escondeu. O servidor também recusa (409 INVENTARIO_AUTOAPROVACAO), inclusive para quem só enviou.
 */
export function podeVerRevisao(entrada: EntradaPodeAprovar): boolean {
  if (entrada.usuarioId === null) return false;
  if (!entrada.permissoes.includes(PERMISSAO_APROVAR_AJUSTE)) return false;
  return !ehAutorDaContagem(entrada.usuarioId, entrada.autoresContagemIds);
}

/** Aviso no lugar da revisão para quem participou da contagem. */
export const MENSAGEM_PARTICIPANTE_NAO_VE_REVISAO =
  'Aguardando aprovação de outra pessoa. Quem participou da contagem não vê a revisão nem descarta esta contagem.';

/** Aprovar exige `estoque:aprovar-ajuste` e não ser autor de nenhuma linha da contagem. */
export function podeAprovar(entrada: EntradaPodeAprovar): boolean {
  if (entrada.usuarioId === null) return false;
  if (!entrada.permissoes.includes(PERMISSAO_APROVAR_AJUSTE)) return false;
  return !ehAutorDaContagem(entrada.usuarioId, entrada.autoresContagemIds);
}

/** Por que "Aprovar ajustes" está desabilitado (nulo quando pode aprovar). */
export function motivoNaoPodeAprovar(entrada: EntradaPodeAprovar): string | null {
  if (!entrada.permissoes.includes(PERMISSAO_APROVAR_AJUSTE)) return MENSAGEM_SEM_PERMISSAO_APROVAR;
  if (ehAutorDaContagem(entrada.usuarioId, entrada.autoresContagemIds)) return MENSAGEM_AUTOR_NAO_APROVA;
  return null;
}

/** Em que o usuário participou da contagem, quando participou (explica o bloqueio). */
export function textoParticipacao(autores: readonly AutorContagem[], usuarioId: string | null, formatarDataHora: (iso: string) => string): string | null {
  if (usuarioId === null) return null;
  const autor = autores.find((candidato) => candidato.userId === usuarioId);
  if (!autor) return null;
  return `Você salvou linhas desta contagem (primeiro salvamento em ${formatarDataHora(autor.primeiroSalvamentoEm)}).`;
}

/** Nomes de todos os participantes da contagem e o remetente (que pode não ter contado). */
export function textoParticipantes(autores: readonly AutorContagem[], enviadoPorNome: string | null): { readonly contagem: string | null; readonly remetente: string | null } {
  const contagem = autores.length > 0 ? autores.map((autor) => autor.nome).join(', ') : null;
  return { contagem, remetente: enviadoPorNome };
}

// ---------------------------------------------------------------------------------------------------------------------
// Justificativas por linha
// ---------------------------------------------------------------------------------------------------------------------

export type JustificativasRevisao = Readonly<Record<string, string>>;

/** A linha exige justificativa do aprovador? Saldo de abertura (sobra na abertura) recebe a justificativa fixa. */
export function exigeJustificativa(item: Pick<ItemRevisao, 'tipoAjuste'>): boolean {
  return item.tipoAjuste !== 'NENHUM' && item.tipoAjuste !== 'SALDO_ABERTURA';
}

/** Justificativa inicial da tela: o que o servidor já guardou (ex.: tentativa anterior); vazio caso contrário. */
export function justificativasIniciais(itens: readonly ItemRevisao[]): JustificativasRevisao {
  const mapa: Record<string, string> = {};
  for (const item of itens) {
    if (exigeJustificativa(item)) mapa[item.itemId] = item.justificativa ?? '';
  }
  return mapa;
}

/** Linhas com diferença que ainda não têm justificativa (a abertura é dispensada). */
export function justificativasPendentes(itens: readonly ItemRevisao[], justificativas: JustificativasRevisao): readonly string[] {
  return itens.filter((item) => exigeJustificativa(item) && (justificativas[item.itemId] ?? '').trim() === '').map((item) => item.itemId);
}

/** Corpo do POST /aprovar: uma justificativa (aparada) por linha que a exige. */
export function montarAprovar(versao: number, itens: readonly ItemRevisao[], justificativas: JustificativasRevisao): AprovarInventarioInput {
  return {
    versao,
    justificativas: itens
      .filter((item) => exigeJustificativa(item))
      .map((item) => ({ itemId: item.itemId, justificativa: (justificativas[item.itemId] ?? '').trim() })),
  };
}

/** Justificativa mostrada na linha: fixa na abertura; a digitada nas demais com diferença; nada sem diferença. */
export function justificativaFixa(item: Pick<ItemRevisao, 'tipoAjuste' | 'justificativaAutomatica'>): string | null {
  return item.tipoAjuste === 'SALDO_ABERTURA' ? (item.justificativaAutomatica ?? 'Saldo de abertura') : null;
}

// ---------------------------------------------------------------------------------------------------------------------
// Apoios por linha
// ---------------------------------------------------------------------------------------------------------------------

/** "Impacto no valor" só existe para matéria-prima própria. */
export function mostraImpacto(donoTipo: DonoTipo): boolean {
  return donoTipo === 'PIELTEC';
}

export function apoioDaLinha(item: Pick<ItemRevisao, 'tipoAjuste' | 'localizacaoAlteradaDepoisDoCorte'>): readonly string[] {
  const apoios: string[] = [];
  if (item.tipoAjuste === 'DIVERGENCIA_LOTE') apoios.push(APOIO_SOBRA_LOTE);
  if (item.localizacaoAlteradaDepoisDoCorte) apoios.push(MENSAGEM_LOCALIZACAO_ALTERADA);
  return apoios;
}

const ROTULO_TIPO_AJUSTE: Readonly<Record<TipoAjusteInventario, string>> = {
  SALDO_ABERTURA: 'Entrada de saldo de abertura',
  AJUSTE_ENTRADA: 'Entrada de ajuste',
  AJUSTE_SAIDA: 'Saída de ajuste',
  SAIDA_LOTE: 'Saída de ajuste do lote',
  DIVERGENCIA_LOTE: 'Divergência de lote (sem entrada)',
  NENHUM: 'Sem movimento',
};

export function rotuloTipoAjuste(tipo: TipoAjusteInventario): string {
  return ROTULO_TIPO_AJUSTE[tipo];
}

export interface MovimentoGerado {
  readonly itemId: string;
  readonly codigo: string;
  readonly descricao: string;
  readonly tipo: string;
  readonly quantidade: string;
  readonly justificativa: string;
  /** Divergência de lote não gera movimento: é só registro. */
  readonly geraMovimento: boolean;
}

/** Leitura de um inventário AJUSTADO: o que a aprovação gerou, linha a linha (tipo, quantidade, justificativa). */
export function movimentosGerados(itens: readonly ItemRevisao[]): readonly MovimentoGerado[] {
  return itens
    .filter((item) => item.tipoAjuste !== 'NENHUM')
    .map((item) => ({
      itemId: item.itemId,
      codigo: item.codigo,
      descricao: item.descricao,
      tipo: rotuloTipoAjuste(item.tipoAjuste),
      quantidade: textoDiferenca(item.diferenca, item.unidade),
      justificativa: justificativaFixa(item) ?? item.justificativa ?? '—',
      geraMovimento: item.tipoAjuste !== 'DIVERGENCIA_LOTE',
    }));
}

/** Motivo do descarte só vale preenchido (sem espaços soltos); o botão destrutivo depende disto. */
export function motivoDescarteValido(motivo: string): boolean {
  return motivo.trim().length > 0;
}
